import { useMemo } from "react";

import { useAppSelector } from "@js/hooks";
import type {
  Freelancer,
  FreelancerSkill,
  FreelancerWorkExperience,
} from "@js/types/freelancer";

import {
  useGetFreelancerSkillsQuery,
  useGetFreelancerWorkExperienceQuery,
} from "../../api";
import { useFreelancerProfile } from "../freelancer-profile";

export type ProfileCompletionAreaData = {
  area: EnumType<typeof ENUMS.ProfileCompletionArea>;
  progress: number;
  itemsCount: number | null;
};

const AREAS: Array<
  ProfileCompletionAreaData & {
    isCompleted: ({
      freelancer,
      freelancerSkills,
      freelancerWorkExperience,
    }: {
      freelancer: Freelancer;
      freelancerWorkExperience: FreelancerWorkExperience[];
      freelancerSkills: FreelancerSkill[];
    }) => boolean;
  }
> = [
  {
    area: ENUMS.ProfileCompletionArea.FreelancerLocationCompletionDetail,
    progress: 15,
    isCompleted: ({ freelancer }) => !!freelancer.location_full,
    itemsCount: null,
  },
  {
    area: ENUMS.ProfileCompletionArea.FreelancerRoleCompletionDetail,
    progress: 30,
    isCompleted: ({ freelancer }) => !!freelancer.role,
    itemsCount: null,
  },
  {
    area: ENUMS.ProfileCompletionArea.FreelancerSkillCompletionDetail,
    progress: 15,
    isCompleted: ({ freelancerSkills }) => !!freelancerSkills?.length,
    itemsCount: 5,
  },
  {
    area: ENUMS.ProfileCompletionArea.FreelancerSuperPowersCompletionDetail,
    progress: 15,
    isCompleted: ({ freelancerSkills }) =>
      !!freelancerSkills?.filter((skill) => skill.is_superpower).length,
    itemsCount: 3,
  },
  {
    area: ENUMS.ProfileCompletionArea.FreelancerWorkExperienceCompletionDetail,
    progress: 15,
    isCompleted: ({ freelancerWorkExperience }) =>
      !!freelancerWorkExperience?.length,
    itemsCount: null,
  },
  {
    area: ENUMS.ProfileCompletionArea.FreelancerFullNameCompletionDetail,
    progress: 10,
    isCompleted: ({ freelancer }) => !!freelancer.user.public_name,
    itemsCount: null,
  },
];

const getCompletionAreasData = ({
  freelancer,
  freelancerSkills,
  freelancerWorkExperience,
}: {
  freelancer: Freelancer;
  freelancerWorkExperience: FreelancerWorkExperience[];
  freelancerSkills: FreelancerSkill[];
}) => {
  const missing: ProfileCompletionAreaData[] = [];
  let completion_percent: number = 0;
  AREAS.forEach((area) => {
    if (
      area.isCompleted({
        freelancer,
        freelancerSkills,
        freelancerWorkExperience,
      })
    ) {
      completion_percent += area.progress;
    } else {
      missing.push(area);
    }
  });

  return { missing, completion_percent };
};

export const useCurrentFreelancerProfileCompletion = ({
  skip,
}: { skip?: boolean } = {}) => {
  const freelancer = useFreelancerProfile();
  const isFetchingFreelancerProfile = useAppSelector(
    (state) => state.freelancer.fetchingFreelancerProfile,
  );
  const freelancerId = freelancer?.id as number;
  const commonSkip = !freelancerId || skip;

  const {
    data: freelancerWorkExperience,
    isLoading: isLoadingFreelancerWorkExperience,
    isFetching: isFetchingWorkExperience,
  } = useGetFreelancerWorkExperienceQuery(undefined, {
    skip: commonSkip,
  });

  const {
    data: freelancerSkills,
    isLoading: isLoadingFreelancerSkills,
    isFetching: isFetchingSkills,
  } = useGetFreelancerSkillsQuery({ id: freelancerId }, { skip: commonSkip });

  const areasData = useMemo(() => {
    if (!freelancerSkills || !freelancerWorkExperience || !freelancer) {
      return;
    }

    return getCompletionAreasData({
      freelancerSkills,
      freelancerWorkExperience,
      freelancer,
    });
  }, [freelancer, freelancerSkills, freelancerWorkExperience]);

  const isLoading =
    !freelancer ||
    isLoadingFreelancerSkills ||
    isLoadingFreelancerWorkExperience;
  const isFetching =
    isFetchingFreelancerProfile || isFetchingWorkExperience || isFetchingSkills;

  const isProfileCompleted =
    !!areasData &&
    (areasData.completion_percent >= 100 || !areasData.missing.length);

  return { data: areasData, isLoading, isFetching, isProfileCompleted };
};
